import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

import { T } from '@flipster/i18n';
import type { PswapOrder } from '@flipster/r230522/model/prex.v2.r230522.tables.private_orders';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';

import { MovableModal } from '../shared/movable-modal';
import { SymbolScope } from '../shared/symbol/bunja';
import { tradingSessionCheckBunja } from '../shared/trading-suspension/state';
import { SelectedTpslOrderContext, tpslModalBunja, type TpslModalSteps } from './state';
import { TpslFormContent } from './tpsl-form-content';
import { TpslManageContent } from './tpsl-manage-content';
import { TpslTab } from './tpsl-tab';

interface TpslModalProps {
  isOpen: boolean;
  externalOnClose: () => void;
  onExit?: () => void;
}

const Steps = createStepComponent<TpslModalSteps>();

const modalHeight = 760;
const headerHeight = 44;
const contentHeight = modalHeight - headerHeight;

export const TpslModal = ({ isOpen, externalOnClose: onClose, onExit }: TpslModalProps) => {
  const {
    tpslType,
    canPartial,
    initialStepAtom,
    entireOrdersAtom,
    hasTpslOrderAtom,
    partialOrdersCountAtom,
    symbol,
    dismissalGuardAtom,
    openedViaDrag,
  } = useBunja(tpslModalBunja);
  // Subscribe to the guard so the throw fires when the underlying open position is gone;
  // the outer ModalErrorBoundary in use-tpsl-modal-overlay.tsx catches it and closes the modal.
  useAtomValue(dismissalGuardAtom);
  // initialStepAtom은 selectAtom(orderId/step)으로 참조 안정화됨.
  // entire 탭 수정 진입은 entireOrdersAtom에서 최신 주문을 lazy read (orderId-stable atom은 필드 stale 가능).
  const initialValue = useAtomValue(initialStepAtom);
  const hasTpslOrder = useAtomValue(hasTpslOrderAtom);
  const partialOrdersCount = useAtomValue(partialOrdersCountAtom);

  const { checkTradable } = useBunja(tradingSessionCheckBunja, [SymbolScope.bind(symbol)]);

  const { current, push } = useSteps<TpslModalSteps>({ initialValue });

  const hideTab = useMemo(() => {
    if (!canPartial) return true;
    if (hasTpslOrder === 'empty') return false;
    if (current.step === 'partial-add') return partialOrdersCount > 0;
    if (current.step === 'partial-update') return true;
    return false;
  }, [canPartial, hasTpslOrder, current.step, partialOrdersCount]);

  const handleClose = useCallback(() => {
    switch (current.step) {
      case 'partial-add':
        if (partialOrdersCount === 0) return onClose();
        return push({ step: 'partial-manage', context: undefined });
      case 'partial-update':
        return push({ step: 'partial-manage', context: undefined });
      case 'partial-manage':
      case 'entire-add':
      case 'entire-update':
      default:
        return onClose();
    }
  }, [current.step, onClose, push, partialOrdersCount]);

  const handleTabChange = useAtomCallback(
    useCallback(
      (get, _set, tab: 'entire' | 'partial') => {
        if (tab === 'entire') {
          // entireOrderAtom(orderId-stable)이 아니라 원본 리스트에서 클릭 시점 최신 주문을 읽는다.
          const entireOrders = get(entireOrdersAtom);
          const entireOrder = entireOrders.length > 0 ? entireOrders[0] : undefined;
          push(
            entireOrder
              ? { step: 'entire-update', context: { tpslOrder: entireOrder } }
              : { step: 'entire-add', context: undefined },
          );
        } else {
          push(
            partialOrdersCount === 0
              ? { step: 'partial-add', context: undefined }
              : { step: 'partial-manage', context: undefined },
          );
        }
      },
      [entireOrdersAtom, push, partialOrdersCount],
    ),
  );

  return (
    <MovableModal.Root onClose={handleClose} isOpen={isOpen} onExit={onExit}>
      <div className={css({ h: current.step === 'partial-manage' ? contentHeight : 'initial' })}>
        <MovableModal.Header>
          <MovableModal.Title
            label={
              tpslType === 'tp' ? (
                <T id='Autogen_common.r251126.s_Take_Profit' />
              ) : (
                <T id='Autogen_common.r251126.s_Stop_Loss' />
              )
            }
          />
        </MovableModal.Header>
        {!hideTab && (
          <MovableModal.Content css={{ pb: 24 }}>
            <TpslTab onChange={handleTabChange} />
          </MovableModal.Content>
        )}
        <Steps model={current}>
          <Steps.Step step='entire-add'>
            {() => (
              <TpslFormContent
                onConfirm={() => {
                  if (!checkTradable({ onClosePreviousModal: onClose })) return;
                  onClose();
                }}
              />
            )}
          </Steps.Step>
          <Steps.Step step='entire-update'>
            {({ tpslOrder }) => (
              <SelectedTpslOrderContext value={tpslOrder}>
                <TpslFormContent
                  onConfirm={() => {
                    if (!checkTradable({ onClosePreviousModal: onClose })) return;
                    onClose();
                  }}
                />
              </SelectedTpslOrderContext>
            )}
          </Steps.Step>
          <Steps.Step step='partial-add'>
            {() => (
              <TpslFormContent
                onConfirm={() => {
                  if (!checkTradable({ onClosePreviousModal: handleClose })) return;
                  if (openedViaDrag) return onClose();
                  push({ step: 'partial-manage', context: undefined });
                }}
              />
            )}
          </Steps.Step>
          <Steps.Step step='partial-update'>
            {({ tpslOrder }) => (
              <SelectedTpslOrderContext value={tpslOrder}>
                <TpslFormContent
                  onConfirm={() => {
                    if (!checkTradable({ onClosePreviousModal: handleClose })) return;
                    if (openedViaDrag) return onClose();
                    push({ step: 'partial-manage', context: undefined });
                  }}
                />
              </SelectedTpslOrderContext>
            )}
          </Steps.Step>
          <Steps.Step step='partial-manage'>
            {() => (
              <TpslManageContent
                onClickAdd={() => {
                  if (!checkTradable({ onClosePreviousModal: handleClose })) return;
                  push({ step: 'partial-add', context: undefined });
                }}
                onClickModify={(tpslOrder: PswapOrder) => {
                  if (!checkTradable({ onClosePreviousModal: handleClose })) return;
                  push({ step: 'partial-update', context: { tpslOrder } });
                }}
              />
            )}
          </Steps.Step>
        </Steps>
      </div>
    </MovableModal.Root>
  );
};
